import React, { useState } from "react";
import {
  Modal,
  Box,
  Text,
  Button,
  Select,
  TextInput,
  createStyles,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useSelector } from "react-redux";
import {
  LuChevronDown,
  LuShieldCheck,
  LuArrowLeft,
  LuCheckCircle2,
  LuAlertTriangle,
} from "react-icons/lu";
import swal from "sweetalert";
import moment from "moment";
import { getelibilitystatus } from "../../services";
import { showError } from "../../utils";

/* ─── full ClaimMD allowed service code list ─────────────────────────────── */
const ALLOWED_SERVICE_CODES = [
  "1","2","3","4","5","6","7","8","9","10","11","12","13","14","15","16",
  "17","18","19","20","21","22","23","24","25","26","27","28","30","32",
  "33","34","35","36","37","38","39","40","41","42","43","44","45","46",
  "47","48","49","50","51","52","53","54","55","56","57","58","59","60",
  "61","62","63","64","65","66","67","68","69","70","71","72","73","74",
  "75","76","77","78","79","80","81","82","83","84","85","86","87","88",
  "89","90","91","92","93","94","95","96","97","98","99","A0","A1","A3",
  "A4","A5","A6","A7","A8","A9","AA","AB","AC","AD","AE","AF","AG","AH",
  "AI","AJ","AK","AL","AM","AN","AO","AP","AQ","AR","B1","B2","B3","BA",
  "BB","BC","BD","BE","BF","BG","BH","BI","BJ","BK","BL","BM","BN","BP",
  "BQ","BR","BS","BT","BU","BV","BW","BX","BY","BZ","C1","CA","CB","CC",
  "CD","CE","CF","CG","CH","CI","CJ","CK","CL","CM","CN","CO","CP","CQ",
  "DG","DM","DS","GF","GN","GY","IC","MH","NI","ON","PT","PU","RN","RT",
  "TC","TN","UC","E0","E1","E2","E3","E4","E5","E6","E7","E8","E9","E10",
  "E11","E12","E13","E14","E15","E16","E17","E18","E19","E20","E21","E22",
  "E23","E24","E25","E26","E27","E28","E29","E30","E31","E32","E33","E34",
  "E35","E36","EA","EB","EC","ED","EE","EF","SMH",
];

const SERVICE_CODE_OPTIONS = ALLOWED_SERVICE_CODES.map((c) => ({ value: c, label: c }));

const RELATIONSHIP_OPTIONS = [
  { value: "18", label: "Self" },
  { value: "01", label: "Spouse" },
  { value: "19", label: "Child" },
  { value: "G8", label: "Other" },
];

const GENDER_OPTIONS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
];

/* ─── result helpers ─────────────────────────────────────────────────────── */
const fmtD = (d, fmt = "YYYYMMDD") =>
  d ? moment(d, fmt).format("MM/DD/YYYY") : "—";

const fmtDRange = (rangeStr) => {
  if (!rangeStr) return "—";
  const [start, end] = rangeStr.split("-");
  const s = moment(start, "YYYYMMDD").format("MM/DD/YYYY");
  if (!end || end === "Invalid date") return s;
  const e = moment(end, "YYYYMMDD");
  return e.isValid() ? `${s} – ${e.format("MM/DD/YYYY")}` : s;
};

const relLabel = (val) =>
  RELATIONSHIP_OPTIONS.find((o) => o.value === val)?.label || val || "—";

const genderLabel = (val) => {
  if (val === "M") return "Male";
  if (val === "F") return "Female";
  return val || "—";
};

/* ─── styles ─────────────────────────────────────────────────────────────── */
const useStyles = createStyles(() => ({
  modalBody: {
    padding: "22px 28px !important",
    fontFamily: "Inter, sans-serif",
  },
  eyebrow: {
    margin: "0 0 6px",
    fontSize: "11.5px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#667085",
    fontFamily: "Inter, sans-serif",
  },
  headerTitle: {
    margin: 0,
    fontSize: "22px",
    fontWeight: 700,
    color: "#171717",
    letterSpacing: "-0.018em",
    lineHeight: 1.2,
    fontFamily: "Inter, sans-serif",
  },
  headerSub: {
    margin: "4px 0 0",
    fontSize: "13px",
    color: "#667085",
    lineHeight: 1.5,
    fontFamily: "Inter, sans-serif",
  },
  sectionTitle: {
    margin: "0 0 14px",
    fontSize: "15px",
    fontWeight: 700,
    color: "#171717",
    fontFamily: "Inter, sans-serif",
  },
  sectionTitleSpaced: {
    margin: "22px 0 14px",
    fontSize: "15px",
    fontWeight: 700,
    color: "#171717",
    fontFamily: "Inter, sans-serif",
  },
  formGrid2: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "16px 18px",
  },
  formGrid3: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "16px 18px",
    marginTop: "14px",
  },
  formGrid2Spaced: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "16px 18px",
    marginTop: "14px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontSize: "12.5px",
    fontWeight: 600,
    color: "#262626",
    marginBottom: "6px",
    lineHeight: 1.4,
    fontFamily: "Inter, sans-serif",
  },
  req: { color: "#DC2626" },
  selectBox: {
    "& .mantine-Select-input": {
      height: "42px",
      borderRadius: "8px",
      border: "1px solid #E5E5E5",
      fontSize: "13px",
      color: "#171717",
      backgroundColor: "#fff",
      paddingLeft: "12px",
      fontFamily: "Inter, sans-serif",
      transition: "border-color .12s ease, box-shadow .12s ease",
      "&:focus": {
        borderColor: "#171717",
        boxShadow: "0 0 0 3px rgba(23,23,23,0.08)",
      },
    },
    "& .mantine-Select-rightSection": { pointerEvents: "none", color: "#171717" },
  },
  textInput: {
    "& .mantine-TextInput-input": {
      height: "42px",
      borderRadius: "8px",
      border: "1px solid #E5E5E5",
      fontSize: "13px",
      color: "#171717",
      backgroundColor: "#fff",
      paddingLeft: "12px",
      fontFamily: "Inter, sans-serif",
      transition: "border-color .12s ease, box-shadow .12s ease",
      "&:focus": {
        borderColor: "#171717",
        boxShadow: "0 0 0 3px rgba(23,23,23,0.08)",
      },
    },
  },
  dateInput: {
    "& .mantine-DatePickerInput-input": {
      height: "42px",
      borderRadius: "8px",
      border: "1px solid #E5E5E5",
      fontSize: "13px",
      color: "#171717",
      backgroundColor: "#fff",
      paddingLeft: "12px",
      fontFamily: "Inter, sans-serif",
      cursor: "pointer",
      transition: "border-color .12s ease, box-shadow .12s ease",
      "&:focus": {
        borderColor: "#171717",
        boxShadow: "0 0 0 3px rgba(23,23,23,0.08)",
      },
    },
    "& .mantine-DatePickerInput-rightSection": { pointerEvents: "none", color: "#171717" },
  },
  alertInfo: {
    display: "flex",
    gap: "12px",
    padding: "14px 16px",
    borderRadius: "12px",
    border: "1px solid #BFDBFE",
    backgroundColor: "#EFF6FF",
    color: "#1D4ED8",
    fontSize: "12.5px",
    lineHeight: 1.55,
    marginTop: "18px",
    fontFamily: "Inter, sans-serif",
  },
  alertWarn: {
    display: "flex",
    gap: "12px",
    padding: "14px 16px",
    borderRadius: "12px",
    border: "1px solid #FCD34D",
    backgroundColor: "#FEF3C7",
    color: "#B45309",
    fontSize: "12.5px",
    lineHeight: 1.55,
    marginTop: "10px",
    fontFamily: "Inter, sans-serif",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "10px",
    padding: "16px 28px 22px",
    borderTop: "1px solid #F1F1F1",
    margin: "22px -28px -22px",
  },
  cancelBtn: {
    height: "40px",
    borderRadius: "8px",
    fontSize: "13.5px",
    fontWeight: 600,
    fontFamily: "Inter, sans-serif",
    border: "1px solid #E5E5E5",
    color: "#262626",
    backgroundColor: "transparent",
    paddingLeft: "16px",
    paddingRight: "16px",
    "&:hover": { backgroundColor: "#F4F4F5" },
  },
  runBtn: {
    height: "40px",
    borderRadius: "8px",
    fontSize: "13.5px",
    fontWeight: 600,
    fontFamily: "Inter, sans-serif",
    backgroundColor: "#FFBA45",
    color: "#171717",
    paddingLeft: "16px",
    paddingRight: "16px",
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    "&:hover": { backgroundColor: "#EEAA33" },
  },

  /* ── results ── */
  statusRow: {
    paddingTop: "16px",
    paddingBottom: "4px",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "5px 12px",
    borderRadius: "999px",
    fontSize: "13px",
    fontWeight: 700,
    fontFamily: "Inter, sans-serif",
    "& svg": { flexShrink: 0 },
  },
  statusEligible: {
    backgroundColor: "#DCFCE7",
    color: "#15803D",
    border: "1px solid #86EFAC",
  },
  statusNotEligible: {
    backgroundColor: "#FEF2F2",
    color: "#B91C1C",
    border: "1px solid #FCA5A5",
  },
  tabRow: {
    display: "flex",
    gap: "4px",
    paddingTop: "16px",
    borderBottom: "1px solid #E5E5E5",
    marginBottom: "18px",
  },
  tab: {
    height: "34px",
    padding: "0 16px",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "transparent",
    fontSize: "13px",
    fontWeight: 600,
    color: "#667085",
    cursor: "pointer",
    fontFamily: "Inter, sans-serif",
    transition: "color .1s, border-color .1s",
    "&:hover": { color: "#171717" },
  },
  tabActive: {
    color: "#171717",
    borderBottom: "2px solid #FFBA45",
  },
  resultsScroll: {
    maxHeight: "46vh",
    overflowY: "auto",
    paddingRight: "4px",
  },
  infoBlock: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "14px 20px",
    padding: "14px 16px",
    borderRadius: "10px",
    border: "1px solid #E5E5E5",
    background: "#FAFAFA",
  },
  infoBlockSpaced: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "14px 20px",
    padding: "14px 16px",
    borderRadius: "10px",
    border: "1px solid #E5E5E5",
    background: "#FAFAFA",
    marginBottom: "12px",
  },
  infoLabel: {
    fontSize: "11.5px",
    color: "#737373",
    fontFamily: "Inter, sans-serif",
    marginBottom: "2px",
  },
  infoValue: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#262626",
    fontFamily: "Inter, sans-serif",
    wordBreak: "break-word",
  },
  resultsSectionLabel: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#171717",
    fontFamily: "Inter, sans-serif",
    margin: "18px 0 10px",
  },
  noData: {
    fontSize: "13px",
    color: "#737373",
    fontFamily: "Inter, sans-serif",
    padding: "20px 0",
    textAlign: "center",
  },
}));

const ChevronIcon = () => <LuChevronDown size={16} color="#171717" />;

/* ─── component ──────────────────────────────────────────────────────────── */
const VerifyEligibilityModal = ({ opened, onClose, patient, onVerified }) => {
  const { classes, cx } = useStyles();
  const [loading, setLoading]       = useState(false);
  const [view, setView]             = useState("form");   // "form" | "results"
  const [activeTab, setActiveTab]   = useState("plan");
  const [resultData, setResultData] = useState(null);

  const { profile }       = useSelector((state) => state?.profile);
  const { allpayer }      = useSelector((state) => state.payer);
  const allbillingprovider = useSelector(
    (state) => state.billingprovider?.allbillingprovider
  );

  const providerOptions = (allbillingprovider?.billingprovider || []).map((p) => ({
    value: p.id,
    label: p.name || p.prov_name || p.prov_npi || "Unknown",
  }));

  const form = useForm({
    initialValues: {
      firstName:    "",
      lastName:     "",
      dob:          null,
      gender:       "",
      memberId:     "",
      relationship: "18",
      providerId:   allbillingprovider?.billingprovider?.[0]?.id || "",
      payerId:      "",
      serviceDate:  null,
      serviceType:  "30",
      procCode:     "",
    },
  });

  // Prefill form when modal opens with a specific patient
  React.useEffect(() => {
    if (opened && patient) {
      const nameParts = (patient?.name || "").trim().split(/\s+/);
      const firstName = nameParts[0] || "";
      const lastName  = nameParts.slice(1).join(" ") || "";

      const dobRaw = patient?.form1?.dob;
      const dob    = dobRaw ? new Date(dobRaw) : null;

      const genderRaw = (patient?.form1?.gender || "").toLowerCase();
      const gender = genderRaw.startsWith("m") ? "M" : genderRaw.startsWith("f") ? "F" : "";

      const memberId = patient?.form2?.BillingInsurance?.[0]?.identificationNumber || "";

      const payerId = patient?.form1?.insurancepayer?.value || "";

      const defaultProviderId = allbillingprovider?.billingprovider?.[0]?.id || "";

      form.setValues({ firstName, lastName, dob, gender, memberId, payerId, providerId: defaultProviderId });
    }
  }, [opened, patient]);

  // Fix: providerId set on mount may be empty if providers not loaded yet — update when data arrives
  React.useEffect(() => {
    const firstId = allbillingprovider?.billingprovider?.[0]?.id;
    if (firstId && !form.values.providerId) {
      form.setFieldValue("providerId", firstId);
    }
  }, [allbillingprovider]);

  const handleClose = () => {
    form.reset();
    setView("form");
    setActiveTab("plan");
    setResultData(null);
    onClose();
  };

  const onSubmit = async () => {
    const v = form.values;

    const missing = [];
    if (!v.firstName)    missing.push("First name");
    if (!v.lastName)     missing.push("Last name");
    if (!v.dob)          missing.push("Date of birth");
    if (!v.gender)       missing.push("Gender");
    if (!v.memberId)     missing.push("Member ID");
    if (!v.relationship) missing.push("Relationship to insured");
    if (!v.payerId)      missing.push("Payer");
    if (!v.serviceDate)  missing.push("Service date");

    if (missing.length > 0) {
      await swal({
        title: "Please fill out the following fields",
        text: missing.join(", "),
        icon: "warning",
        dangerMode: true,
      });
      return;
    }

    if (v.serviceType && !ALLOWED_SERVICE_CODES.includes(v.serviceType)) {
      await swal({
        title: "Incorrect Service Code",
        text: "Please select a valid service code from the list.",
        icon: "warning",
        dangerMode: true,
      });
      return;
    }

    const selectedProvider = (allbillingprovider?.billingprovider || []).find(
      (p) => p.id === v.providerId
    ) || allbillingprovider?.billingprovider?.[0];

    if (
      v.procCode &&
      selectedProvider?.services?.length > 0 &&
      !selectedProvider.services.some((s) => s?.servicenumber === v.procCode)
    ) {
      await swal({
        title: "Incorrect Procedure Code",
        text: "Please enter a valid procedure code for the selected provider.",
        icon: "warning",
        dangerMode: true,
      });
      return;
    }

    const proceed = await swal({
      title: "Run eligibility check?",
      text: "Each eligibility check is billed at $5.00 and will be added to your next invoice.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Yes, Proceed"],
    });

    if (!proceed) return;

    const payload = {
      ins_name_f:   v.firstName,
      ins_name_l:   v.lastName,
      payerid:      v.payerId,
      pat_rel:      v.relationship,
      fdos:         moment(v.serviceDate).format("YYYYMMDD"),
      prov_npi:     selectedProvider?.prov_npi || "1111111112",
      prov_taxid:   selectedProvider?.federalTaxID?.number || "208655599",
      service_code: v.serviceType || "30",
      ins_number:   v.memberId,
      ins_dob:      moment(v.dob).format("YYYYMMDD"),
      ins_sex:      v.gender,
      providerid:   selectedProvider?.id,
      admin:        profile?.user?.id,
    };

    if (v.procCode) payload.proc_code = v.procCode;

    try {
      setLoading(true);
      const res = await getelibilitystatus(payload);

      if (res?.data?.notallowed) {
        await swal({
          title: res?.data?.message,
          text: res?.data?.notallowed,
          icon: "warning",
          dangerMode: true,
        });
        return;
      }

      if (res?.data?.eligibility) {
        setResultData(res.data);
        setView("results");
        if (onVerified && patient) {
          onVerified(patient, new Date().toISOString());
        }
      } else if (res?.status === 200) {

      }
    } catch (error) {
      console.error("eligibility error", error);
    } finally {
      setLoading(false);
    }
  };

  const payerOptions = (allpayer?.payer || []).map((p) => ({
    value: p.id,
    label: `${p.payerName}${p.payerId ? " · " + p.payerId : ""}`,
  }));

  const selectedPayerName =
    allpayer?.payer?.find((p) => p.id === form.values.payerId)?.payerName || null;

  const patientName =
    [form.values.firstName, form.values.lastName].filter(Boolean).join(" ");

  const elig       = resultData?.eligibility;
  const isEligible = resultData?.message === "Eligible" && elig != null;

  /* ── plan details tab ── */
  const renderPlanDetails = () => (
    <Box>
      <Text className={classes.resultsSectionLabel}>Search Criteria</Text>
      <Box className={classes.infoBlock}>
        <Box>
          <div className={classes.infoLabel}>Subscriber First Name</div>
          <div className={classes.infoValue}>{elig?.data?.ins_name_f || "—"}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Subscriber Last Name</div>
          <div className={classes.infoValue}>{elig?.data?.ins_name_l || "—"}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Date of Birth</div>
          <div className={classes.infoValue}>{fmtD(elig?.data?.ins_dob)}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Gender</div>
          <div className={classes.infoValue}>{genderLabel(elig?.data?.ins_sex)}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Member ID</div>
          <div className={classes.infoValue}>{elig?.data?.ins_number || "—"}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Patient Relationship</div>
          <div className={classes.infoValue}>{relLabel(elig?.patientRelationship)}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Date of Service</div>
          <div className={classes.infoValue}>{fmtD(elig?.data?.elig_result_date)}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Payer ID</div>
          <div className={classes.infoValue}>{elig?.payer?.payerId || "—"}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Provider Tax ID</div>
          <div className={classes.infoValue}>{elig?.provider?.federalTaxID?.number || "—"}</div>
        </Box>
      </Box>

      <Text className={classes.resultsSectionLabel}>Payer Response · Subscriber Information</Text>
      <Box className={classes.infoBlock}>
        <Box>
          <div className={classes.infoLabel}>Member First Name</div>
          <div className={classes.infoValue}>{elig?.data?.ins_name_f || "—"}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Member Last Name</div>
          <div className={classes.infoValue}>{elig?.data?.ins_name_l || "—"}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Member ID</div>
          <div className={classes.infoValue}>{elig?.data?.ins_number || "—"}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Date of Birth</div>
          <div className={classes.infoValue}>{fmtD(elig?.data?.ins_dob)}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Gender</div>
          <div className={classes.infoValue}>{genderLabel(elig?.data?.ins_sex)}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Group Number</div>
          <div className={classes.infoValue}>{elig?.data?.group_number || "—"}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Plan Begin Date</div>
          <div className={classes.infoValue}>
            {fmtD(elig?.data?.plan_begin_date?.split("-")?.[0])}
          </div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Eligibility Date Range</div>
          <div className={classes.infoValue}>{fmtDRange(elig?.data?.plan_begin_date)}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Result Time</div>
          <div className={classes.infoValue}>
            {elig?.data?.elig_result_time
              ? moment(elig.data.elig_result_time, "HHmm").format("hh:mm A")
              : "—"}
          </div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Address</div>
          <div className={classes.infoValue}>{elig?.data?.ins_addr_1 || "—"}</div>
        </Box>
      </Box>

      <Text className={classes.resultsSectionLabel}>Active Coverage · Plan Details</Text>
      <Box className={classes.infoBlock}>
        <Box>
          <div className={classes.infoLabel}>Payer Name</div>
          <div className={classes.infoValue}>{elig?.payer?.payerName || "—"}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Payer ID</div>
          <div className={classes.infoValue}>{elig?.payer?.payerId || "—"}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Plan Name / Number</div>
          <div className={classes.infoValue}>{elig?.data?.plan_number || "—"}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Last Updated</div>
          <div className={classes.infoValue}>{fmtD(elig?.data?.elig_result_date)}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Patient / Client Number</div>
          <div className={classes.infoValue}>{elig?.data?.group_number || "—"}</div>
        </Box>
        <Box>
          <div className={classes.infoLabel}>Medicare ID</div>
          <div className={classes.infoValue}>{elig?.payer?.payerId || "—"}</div>
        </Box>
      </Box>
    </Box>
  );

  /* ── benefit details tab ── */
  const renderBenefitDetails = () => {
    const benefit = elig?.benefit;
    if (!benefit || benefit.length === 0) {
      return <div className={classes.noData}>No benefit data returned for this check.</div>;
    }
    return (
      <Box>
        {benefit.slice(0, 8).map((b, i) => (
          <Box key={i} className={classes.infoBlockSpaced}>
            <Box>
              <div className={classes.infoLabel}>Benefit Amount</div>
              <div className={classes.infoValue}>{b?.benefit_amount || "—"}</div>
            </Box>
            <Box>
              <div className={classes.infoLabel}>Benefit Code</div>
              <div className={classes.infoValue}>{b?.benefit_code || "—"}</div>
            </Box>
            <Box>
              <div className={classes.infoLabel}>Benefit Level</div>
              <div className={classes.infoValue}>
                {b?.benefit_level_description || b?.benefit_level_code || "—"}
              </div>
            </Box>
            <Box>
              <div className={classes.infoLabel}>Coverage Description</div>
              <div className={classes.infoValue}>
                {b?.benefit_coverage_description || "—"}
              </div>
            </Box>
            <Box>
              <div className={classes.infoLabel}>Benefit Period</div>
              <div className={classes.infoValue}>{b?.benefit_period_code || "—"}</div>
            </Box>
            <Box>
              <div className={classes.infoLabel}>In-Plan Network</div>
              <div className={classes.infoValue}>{b?.inplan_network || "—"}</div>
            </Box>
            <Box>
              <div className={classes.infoLabel}>Insurance Plan</div>
              <div className={classes.infoValue}>{b?.insurance_plan || "—"}</div>
            </Box>
            <Box>
              <div className={classes.infoLabel}>Insurance Type</div>
              <div className={classes.infoValue}>
                {b?.insurance_type_description || b?.insurance_type_code || "—"}
              </div>
            </Box>
            {b?.benefit_description && (
              <Box>
                <div className={classes.infoLabel}>Benefit Description</div>
                <div className={classes.infoValue}>{b.benefit_description}</div>
              </Box>
            )}
            {b?.benefit_notes && (
              <Box>
                <div className={classes.infoLabel}>Notes</div>
                <div className={classes.infoValue}>{b.benefit_notes}</div>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  /* ─────────────────────────────────────────────────────────────────────── */
  return (
    <Modal
      opened={opened}
      size={700}
      onClose={handleClose}
      overlayProps={{ blur: 3 }}
      radius="lg"
      zIndex={1000}
      centered
      classNames={{ body: classes.modalBody }}
      title={
        <Box>
          <Text className={classes.eyebrow}>Eligibility Check</Text>
          <Text className={classes.headerTitle}>
            {view === "results" ? "Eligibility Results" : "Verify eligibility"}
          </Text>
          <Text className={classes.headerSub}>
            {view === "results"
              ? `${patientName || "Patient"} · ${
                  selectedPayerName ||
                  elig?.payer?.payerName ||
                  "payer"
                }`
              : patientName && selectedPayerName
              ? `Running check for ${patientName} with ${selectedPayerName}.`
              : "Run a real-time eligibility check against your payer."}
          </Text>
        </Box>
      }
      styles={{
        header: {
          padding: "22px 28px",
          borderBottom: "1px solid #F1F1F1",
          alignItems: "flex-start",
          gap: "16px",
        },
        close: { color: "#262626" },
      }}
    >
      {view === "form" ? (
        <>
          {/* ── Subscriber ── */}
          <Text className={classes.sectionTitle}>Subscriber</Text>

          <Box className={classes.formGrid2}>
            <Box className={classes.field}>
              <Text className={classes.label}>
                First name{" "}
                <Text component="span" className={classes.req}>*</Text>
              </Text>
              <TextInput
                className={classes.textInput}
                placeholder="First name"
                {...form.getInputProps("firstName")}
              />
            </Box>
            <Box className={classes.field}>
              <Text className={classes.label}>
                Last name{" "}
                <Text component="span" className={classes.req}>*</Text>
              </Text>
              <TextInput
                className={classes.textInput}
                placeholder="Last name"
                {...form.getInputProps("lastName")}
              />
            </Box>
          </Box>

          <Box className={classes.formGrid3}>
            <Box className={classes.field}>
              <Text className={classes.label}>
                Date of birth{" "}
                <Text component="span" className={classes.req}>*</Text>
              </Text>
              <DatePickerInput
                className={classes.dateInput}
                placeholder="MM/DD/YYYY"
                valueFormat="MM/DD/YYYY"
                rightSection={<ChevronIcon />}
                popoverProps={{ zIndex: 1100 }}
                {...form.getInputProps("dob")}
              />
            </Box>
            <Box className={classes.field}>
              <Text className={classes.label}>
                Gender{" "}
                <Text component="span" className={classes.req}>*</Text>
              </Text>
              <Select
                className={classes.selectBox}
                placeholder="Select gender"
                data={GENDER_OPTIONS}
                rightSection={<ChevronIcon />}
                {...form.getInputProps("gender")}
              />
            </Box>
            <Box className={classes.field}>
              <Text className={classes.label}>
                Member ID{" "}
                <Text component="span" className={classes.req}>*</Text>
              </Text>
              <TextInput
                className={classes.textInput}
                placeholder="Member ID"
                {...form.getInputProps("memberId")}
              />
            </Box>
          </Box>

          <Box className={classes.formGrid2Spaced}>
            <Box className={classes.field}>
              <Text className={classes.label}>
                Relationship to insured{" "}
                <Text component="span" className={classes.req}>*</Text>
              </Text>
              <Select
                className={classes.selectBox}
                placeholder="Select relationship"
                data={RELATIONSHIP_OPTIONS}
                rightSection={<ChevronIcon />}
                {...form.getInputProps("relationship")}
              />
            </Box>
            <Box className={classes.field}>
              <Text className={classes.label}>Provider</Text>
              <Select
                className={classes.selectBox}
                placeholder="Select provider"
                data={providerOptions}
                searchable
                limit={10}
                rightSection={<ChevronIcon />}
                {...form.getInputProps("providerId")}
              />
            </Box>
          </Box>

          {/* ── Coverage ── */}
          <Text className={classes.sectionTitleSpaced}>Coverage</Text>

          <Box className={classes.formGrid2}>
            <Box className={classes.field}>
              <Text className={classes.label}>
                Payer{" "}
                <Text component="span" className={classes.req}>*</Text>
              </Text>
              <Select
                className={classes.selectBox}
                placeholder="Select payer"
                data={payerOptions}
                searchable
                limit={10}
                rightSection={<ChevronIcon />}
                {...form.getInputProps("payerId")}
              />
            </Box>
            <Box className={classes.field}>
              <Text className={classes.label}>
                Service date{" "}
                <Text component="span" className={classes.req}>*</Text>
              </Text>
              <DatePickerInput
                className={classes.dateInput}
                placeholder="MM/DD/YYYY"
                valueFormat="MM/DD/YYYY"
                rightSection={<ChevronIcon />}
                popoverProps={{ zIndex: 1100 }}
                {...form.getInputProps("serviceDate")}
              />
            </Box>
          </Box>

          <Box className={classes.formGrid2Spaced}>
            <Box className={classes.field}>
              <Text className={classes.label}>Service type code</Text>
              <Select
                className={classes.selectBox}
                placeholder="30 (default)"
                data={SERVICE_CODE_OPTIONS}
                searchable
                limit={20}
                rightSection={<ChevronIcon />}
                {...form.getInputProps("serviceType")}
              />
            </Box>
            <Box className={classes.field}>
              <Text className={classes.label}>Procedure code</Text>
              <TextInput
                className={classes.textInput}
                placeholder="Optional"
                {...form.getInputProps("procCode")}
              />
            </Box>
          </Box>

          {/* ── Alerts ── */}
          <Box className={classes.alertInfo}>
            The check typically returns in under 5 seconds. Results are saved to
            the client's eligibility history.
          </Box>
          <Box className={classes.alertWarn}>
            <Box>
              <strong>$5.00 per check.</strong> Each eligibility check is billed
              at $5.00. The charge is added to your next invoice and itemized on
              the receipt.
            </Box>
          </Box>

          {/* ── Footer ── */}
          <Box className={classes.footer}>
            <Button
              className={classes.cancelBtn}
              variant="default"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              className={classes.runBtn}
              onClick={onSubmit}
              loading={loading}
            >
              <LuShieldCheck size={16} />
              Run Eligibility Check ($5.00)
            </Button>
          </Box>
        </>
      ) : (
        <>
          {/* ── Status badge ── */}
          <Box className={classes.statusRow}>
            <span
              className={cx(
                classes.statusBadge,
                isEligible ? classes.statusEligible : classes.statusNotEligible,
              )}
            >
              {isEligible ? (
                <LuCheckCircle2 size={14} />
              ) : (
                <LuAlertTriangle size={14} />
              )}
              {isEligible ? "Eligible" : "Not Eligible / Pending"}
            </span>
          </Box>

          {/* ── Tabs ── */}
          <Box className={classes.tabRow}>
            <button
              type="button"
              className={cx(classes.tab, activeTab === "plan" && classes.tabActive)}
              onClick={() => setActiveTab("plan")}
            >
              Plan Details
            </button>
            <button
              type="button"
              className={cx(classes.tab, activeTab === "benefit" && classes.tabActive)}
              onClick={() => setActiveTab("benefit")}
            >
              Benefit Details
            </button>
          </Box>

          <Box className={classes.resultsScroll}>
            {activeTab === "plan" ? renderPlanDetails() : renderBenefitDetails()}
          </Box>

          {/* ── Footer ── */}
          <Box className={classes.footer}>
            <Button
              className={classes.cancelBtn}
              variant="default"
              leftIcon={<LuArrowLeft size={14} />}
              onClick={() => {
                setView("form");
                setActiveTab("plan");
                setResultData(null);
              }}
            >
              Run Another Check
            </Button>
            <Button className={classes.runBtn} onClick={handleClose}>
              Done
            </Button>
          </Box>
        </>
      )}
    </Modal>
  );
};

export default VerifyEligibilityModal;
